import { useCallback, useEffect, useState } from "react";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { EXPERIMENT_STATES } from "../utils/thorState";
import { useThor } from "../context/ThorContext";

// checkId keys used in computeDesignDoneSet below
const CHECKLIST = {
    design: {
        required: [
            { label: 'Complete Overview (Jira ticket, Spec URL)', tab: 'overview', checkId: 'overview' },
            { label: 'Fill in Hypothesis (If / Then / Because)', tab: 'hypothesis', checkId: 'hypothesis_core' },
            { label: 'Add Strategic Goal (Hypothesis section)', tab: 'hypothesis', checkId: 'strategic_goal' },
            { label: 'Add Background Research (Hypothesis section)', tab: 'hypothesis', checkId: 'background_research' },
            { label: 'Set Days to Power — min. 7 days (Configuration)', tab: 'config', checkId: 'days_to_power' },
            { label: 'Set Target Start Date (Configuration)', tab: 'config', checkId: 'start_date' },
            { label: 'Add Stakeholders — analyst and engineer contacts', tab: 'stakeholders', checkId: 'stakeholders' },
            { label: 'Add at least 2 Buckets (control + treatment)', tab: 'buckets', checkId: 'buckets' },
            { label: 'Add Audience Description (Audiences tab)', tab: 'audiences', checkId: 'audience_desc' },
            { label: 'Add at least 1 Primary Metric', tab: 'metrics', checkId: 'metrics' },
            { label: 'Add Guardrail metrics (1–6)', tab: 'metrics', checkId: 'guardrail_metrics' },
        ],
        prodRequired: [],
        optional: [
            { label: 'Define Audiences (eligibility rules)', tab: 'audiences', checkId: 'audiences' },
            { label: 'Add Variables (feature flag parameters per bucket)', tab: 'variables', checkId: 'variables' },
            { label: 'Add Segments for subgroup analysis (max 3)', tab: 'segments', checkId: 'segments' },
        ],
        next: { label: 'Transition to Design Review', tab: 'run' },
    },
    design_review: {
        required: [
            { label: 'Analyst Design Review — analyst must approve', tab: 'approvals', checkId: 'analyst_review' },
            { label: 'Engineer Design Review — engineer must approve', tab: 'approvals', checkId: 'engineer_review' },
        ],
        prodRequired: [],
        optional: [],
        next: { label: 'Both approvals done → transition to Instrumentation', tab: 'run' },
    },
    instrumentation: {
        required: [
            { label: 'Fill in Instrumentation details (pattern, sources, pages)', tab: 'instrumentation', checkId: 'instrumentation_filled' },
            { label: 'Engineer Instrumentation Signoff — engineer must approve', tab: 'approvals', checkId: 'instrumentation_signoff' },
        ],
        prodRequired: [],
        optional: [],
        next: { label: 'Signoff complete → transition to Pre-prod Validation', tab: 'run' },
    },
    pre_prod_validation: {
        required: [
            { label: 'Owner Pre-prod Signoff — experiment owner must approve', tab: 'approvals', checkId: 'pre_prod_signoff' },
        ],
        prodRequired: [],
        optional: [],
        next: { label: 'Signoff complete → transition to Running', tab: 'run' },
    },
    running: {
        required: [],
        prodRequired: [],
        optional: [],
        next: { label: 'When experiment reaches power → end and decide', tab: 'run' },
    },
    paused: {
        required: [],
        prodRequired: [],
        optional: [],
        next: { label: 'Resume from Run Experiment, or transition to Test Ending', tab: 'run' },
    },
    ended: {
        required: [
            { label: 'Fill in Outcome (required before Clean Up can begin)', tab: 'outcome', checkId: 'outcome_conclusions' },
        ],
        prodRequired: [],
        optional: [
            { label: 'Review analysis results (Analysis tab)', tab: 'analysis' },
        ],
        next: { label: 'Outcome complete → transition to Clean Up', tab: 'run' },
    },
    cleanup: {
        required: [
            { label: 'Engineer Cleanup Signoff — engineer must approve', tab: 'approvals', checkId: 'cleanup_signoff' },
        ],
        prodRequired: [],
        optional: [],
        next: { label: 'Signoff complete → transition to Finished', tab: 'run' },
    },
    finished: null,
    cancelled: null,
};

const filled = (s) => typeof s === 'string' ? s.trim().length > 0 : (s != null && s !== false);

async function computeDesignDoneSet(execute, experimentKey) {
    const [detailData, bucketsData, metricsData, audiencesData, stakeholdersData,
        variablesData, segmentsData] = await Promise.all([
        execute('GET', thor_api_urls.experiment(experimentKey)),
        execute('GET', thor_api_urls.experimentBuckets(experimentKey)).catch(() => null),
        execute('GET', thor_api_urls.experimentMetrics(experimentKey)).catch(() => null),
        execute('GET', thor_api_urls.experimentAudiences(experimentKey)).catch(() => null),
        execute('GET', thor_api_urls.experimentStakeholders(experimentKey)).catch(() => null),
        execute('GET', thor_api_urls.experimentVariables(experimentKey)).catch(() => null),
        execute('GET', thor_api_urls.experimentSegments(experimentKey)).catch(() => null),
    ]);

    const detail = detailData?.experiment ?? detailData ?? {};
    const done = new Set();

    // Required
    if (filled(detail.hypothesisIf) && filled(detail.hypothesisThen) && filled(detail.hypothesisBecause))
        done.add('hypothesis_core');
    if (filled(detail.longTermStrategicGoal)) done.add('strategic_goal');
    if (filled(detail.backgroundResearch)) done.add('background_research');

    const buckets = bucketsData?.buckets ?? bucketsData ?? [];
    if (buckets.length >= 2) done.add('buckets');

    const metrics = metricsData?.metrics ?? metricsData ?? [];
    if (metrics.some(m => ['Primary', 'PRIMARY'].includes(m.category))) done.add('metrics');

    const audiences = audiencesData?.experimentAudiences ?? audiencesData ?? [];
    if (audiences.length > 0) done.add('audiences');
    if (filled(detail.audienceDescription)) done.add('audience_desc');

    if ((filled(detail.jiraTicket) || filled(detail.ticket)) &&
        (filled(detail.specUrl) || filled(detail.specification))) done.add('overview');

    if (filled(detail.targetStartDate)) done.add('start_date');

    // Prod required
    if (metrics.some(m => ['Guardrail', 'GUARDRAIL'].includes(m.category))) done.add('guardrail_metrics');
    if ((detail.daysToPower ?? 0) >= 7) done.add('days_to_power');

    const sMap = stakeholdersData?.stakeholders ?? {};
    if ((sMap.ANALYST?.length ?? 0) > 0 && (sMap.ENGINEER?.length ?? 0) > 0) done.add('stakeholders');

    // Optional
    const variables = variablesData?.variables ?? variablesData ?? [];
    if (variables.length > 0) done.add('variables');

    const segments = segmentsData?.segments ?? segmentsData ?? [];
    if (segments.length > 0) done.add('segments');


    return done;
}

async function computeDesignReviewDoneSet(execute, experimentKey) {
    const reviewsData = await execute('GET', thor_api_urls.experimentReviews(experimentKey)).catch(() => null);
    const reviews = reviewsData?.reviews ?? reviewsData ?? [];
    const done = new Set();
    const analystReview = reviews.find(r => r.reviewType?.toLowerCase() === 'analyst_design_review');
    if (analystReview?.isApproved) done.add('analyst_review');
    const engineerReview = reviews.find(r => r.reviewType?.toLowerCase() === 'engineer_design_review');
    if (engineerReview?.isApproved) done.add('engineer_review');
    return done;
}

async function computeInstrumentationDoneSet(execute, experimentKey) {
    const [reviewsData, instrData] = await Promise.all([
        execute('GET', thor_api_urls.experimentReviews(experimentKey)).catch(() => null),
        execute('GET', thor_api_urls.instrumentationInstructions(experimentKey)).catch(() => null),
    ]);
    const reviews = reviewsData?.reviews ?? reviewsData ?? [];
    const done = new Set();
    const signoff = reviews.find(r => r.reviewType?.toLowerCase() === 'engineer_instrumentation_signoff');
    if (signoff?.isApproved) done.add('instrumentation_signoff');
    if (instrData && (instrData.instrumentationPattern || (instrData.instrumentationSources?.length > 0) || (instrData.pagesUnderTest?.length > 0)))
        done.add('instrumentation_filled');
    return done;
}

async function computePreProdDoneSet(execute, experimentKey) {
    const reviewsData = await execute('GET', thor_api_urls.experimentReviews(experimentKey)).catch(() => null);
    const reviews = reviewsData?.reviews ?? reviewsData ?? [];
    const done = new Set();
    const signoff = reviews.find(r => r.reviewType?.toLowerCase() === 'owner_pre_prod_signoff');
    if (signoff?.isApproved) done.add('pre_prod_signoff');
    return done;
}

async function computeEndedDoneSet(execute, experimentKey) {
    const resultData = await execute('GET', thor_api_urls.experimentResult(experimentKey)).catch(() => null);
    const done = new Set();
    const result = resultData?.result ?? resultData ?? {};
    if (filled(result.conclusions)) done.add('outcome_conclusions');
    return done;
}

async function computeCleanupDoneSet(execute, experimentKey) {
    const reviewsData = await execute('GET', thor_api_urls.experimentReviews(experimentKey)).catch(() => null);
    const reviews = reviewsData?.reviews ?? reviewsData ?? [];
    const done = new Set();
    const signoff = reviews.find(r => r.reviewType?.toLowerCase() === 'engineer_cleanup_signoff');
    if (signoff?.isApproved) done.add('cleanup_signoff');
    return done;
}

const STATES_WITH_COMPLETION = new Set(['design', 'design_review', 'instrumentation', 'pre_prod_validation', 'ended', 'cleanup']);

// All checkIds that belong to each phase (used to auto-mark past phases as done)
const PHASE_ALL_CHECKIDS = {
    design: ['hypothesis_core', 'strategic_goal', 'background_research', 'buckets', 'audience_desc',
             'metrics', 'guardrail_metrics', 'days_to_power', 'stakeholders', 'overview',
             'start_date', 'audiences', 'variables', 'segments'],
    design_review: ['analyst_review', 'engineer_review'],
    instrumentation: ['instrumentation_filled', 'instrumentation_signoff'],
    pre_prod_validation: ['pre_prod_signoff'],
    ended: ['outcome_conclusions'],
    cleanup: ['cleanup_signoff'],
};
const STATE_ORDER = ['design', 'design_review', 'instrumentation', 'pre_prod_validation',
                     'running', 'paused', 'ended', 'cleanup', 'finished', 'cancelled'];

function getAutoDoneIds(currentState) {
    const stateIdx = STATE_ORDER.indexOf(currentState);
    const done = new Set();
    Object.entries(PHASE_ALL_CHECKIDS).forEach(([phase, ids]) => {
        const phaseIdx = STATE_ORDER.indexOf(phase);
        if (phaseIdx >= 0 && stateIdx > phaseIdx) {
            ids.forEach(id => done.add(id));
        }
    });
    return done;
}

const ItemButton = ({ label, done, pendingColor, onClick }) => (
    <button
        type="button"
        className="button is-ghost is-small"
        style={{
            height: 'auto', padding: '0', fontSize: '0.8rem',
            color: done ? '#257942' : pendingColor,
            display: 'block', textAlign: 'left', textDecoration: done ? 'none' : 'underline',
            fontWeight: done ? 500 : 400,
        }}
        onClick={onClick}
    >
        {done ? '✓' : '→'} {label}
    </button>
);

const SectionLabel = ({ children, color }) => (
    <p style={{ fontSize: '0.62rem', fontWeight: 700, color, letterSpacing: '0.07em', marginBottom: '0.3rem', textTransform: 'uppercase' }}>
        {children}
    </p>
);

const ExperimentChecklist = ({ state, experimentKey, onNavigate, onDoneSetChange }) => {
    const { execute } = useFetchWithMsal();
    const { checklistVersion } = useThor();
    const storageKey = experimentKey ? `thor-checklist-open-${experimentKey}` : null;
    const [open, setOpen] = useState(() => {
        if (!storageKey) return true;
        const saved = localStorage.getItem(storageKey);
        return saved === null ? true : saved === 'true';
    });
    const toggleOpen = (val) => {
        const next = typeof val === 'function' ? val(open) : val;
        setOpen(next);
        if (storageKey) localStorage.setItem(storageKey, String(next));
    };
    const [showOptional, setShowOptional] = useState(false);
    const [checkLoading, setCheckLoading] = useState(false);
    const [doneSet, setDoneSet] = useState(null);
    const data = CHECKLIST[state];

    const fetchDone = useCallback(() => {
        const autoDone = getAutoDoneIds(state);
        if (!experimentKey || !STATES_WITH_COMPLETION.has(state)) {
            setDoneSet(autoDone);
            onDoneSetChange?.(autoDone);
            return;
        }
        setCheckLoading(true);
        let computeFn;
        if (state === 'design') computeFn = computeDesignDoneSet;
        else if (state === 'design_review') computeFn = computeDesignReviewDoneSet;
        else if (state === 'instrumentation') computeFn = computeInstrumentationDoneSet;
        else if (state === 'pre_prod_validation') computeFn = computePreProdDoneSet;
        else if (state === 'ended') computeFn = computeEndedDoneSet;
        else if (state === 'cleanup') computeFn = computeCleanupDoneSet;
        computeFn(execute, experimentKey)
            .then(s => {
                const merged = new Set([...autoDone, ...s]);
                setDoneSet(merged);
                onDoneSetChange?.(merged);
            })
            .catch(() => { setDoneSet(autoDone); onDoneSetChange?.(autoDone); })
            .finally(() => setCheckLoading(false));
    }, [execute, experimentKey, state]);

    useEffect(() => { fetchDone(); }, [fetchDone, checklistVersion]);

    if (!data) return null;
    const hasItems = data.required.length > 0 || data.prodRequired.length > 0 || data.optional.length > 0;
    const isDone = (checkId) => checkId && doneSet ? doneSet.has(checkId) : false;

    const completedCount = doneSet !== null
        ? data.required.filter(i => isDone(i.checkId)).length
            + data.prodRequired.filter(i => isDone(i.checkId)).length
        : 0;
    const totalCount = data.required.length + data.prodRequired.length;
    const allDone = completedCount === totalCount && totalCount > 0;

    return (
        <div style={{ borderBottom: '1px solid #dbdbdb', background: '#f8faff' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 1rem' }}>
                <button
                    type="button"
                    aria-expanded={open}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flex: 1, textAlign: 'left' }}
                    onClick={() => toggleOpen(o => !o)}
                >
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#3273dc' }}>
                        📋 Checklist — {EXPERIMENT_STATES[state]?.label ?? state}
                        {doneSet !== null && totalCount > 0 && (
                            <span style={{
                                fontSize: '0.7rem', fontWeight: 600,
                                color: allDone ? '#257942' : '#888',
                                background: allDone ? '#ebf9f1' : '#f5f5f5',
                                borderRadius: '12px', padding: '0.1rem 0.5rem', marginLeft: '0.5rem',
                            }}>
                                {completedCount} / {totalCount} done
                            </span>
                        )}
                        {data.next && (
                            <span style={{ fontWeight: 400, color: '#555', marginLeft: '0.75rem', fontSize: '0.75rem' }}>
                                Next: {data.next.label}
                            </span>
                        )}
                    </span>
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {data.next && (
                        <button
                            type="button"
                            className="button is-link is-small is-light"
                            onClick={e => { e.stopPropagation(); onNavigate?.(data.next.tab); }}
                        >
                            Go to {data.next.tab === 'run' ? 'Run Experiment' : data.next.tab} →
                        </button>
                    )}
                    {STATES_WITH_COMPLETION.has(state) && (
                        <button
                            type="button"
                            className="button is-ghost is-small"
                            style={{ padding: '0 0.25rem', fontSize: '0.75rem', color: '#aaa', height: 'auto' }}
                            title="Refresh completion status"
                            onClick={fetchDone}
                            disabled={checkLoading}
                        >
                            {checkLoading ? '…' : '↺'}
                        </button>
                    )}
                    {hasItems && (
                        <button
                            type="button"
                            className="button is-ghost is-small"
                            style={{ padding: '0 0.25rem', fontSize: '0.75rem', color: '#888', height: 'auto' }}
                            onClick={() => toggleOpen(o => !o)}
                        >
                            {open ? '▲ Hide' : '▼ Show'}
                        </button>
                    )}
                </div>
            </div>

            {open && hasItems && (
                <div style={{ padding: '0 1rem 0.75rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>

                    {data.required.length > 0 && (
                        <div style={{ flex: '1', minWidth: '220px' }}>
                            <SectionLabel color="#363636">Required</SectionLabel>
                            {data.required.map((item, i) => (
                                <div key={i} style={{ marginBottom: '0.15rem' }}>
                                    <ItemButton
                                        label={item.label}
                                        done={isDone(item.checkId)}
                                        pendingColor="#363636"
                                        onClick={e => { e.stopPropagation(); onNavigate?.(item.tab); }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {data.prodRequired.length > 0 && (
                        <div style={{ flex: '1', minWidth: '220px', background: '#fffbeb', borderRadius: '4px', padding: '0.4rem 0.6rem' }}>
                            <SectionLabel color="#946c00">Before going live in PROD</SectionLabel>
                            {data.prodRequired.map((item, i) => (
                                <div key={i} style={{ marginBottom: '0.15rem' }}>
                                    <ItemButton
                                        label={item.label}
                                        done={isDone(item.checkId)}
                                        pendingColor="#946c00"
                                        onClick={e => { e.stopPropagation(); onNavigate?.(item.tab); }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {data.optional.length > 0 && (
                        <div style={{ flex: '1', minWidth: '220px' }}>
                            <button
                                type="button"
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                                onClick={e => { e.stopPropagation(); setShowOptional(o => !o); }}
                            >
                                <SectionLabel color="#aaa">
                                    {showOptional ? '▾' : '▸'} Optional ({data.optional.filter(i => isDone(i.checkId)).length}/{data.optional.length} done)
                                </SectionLabel>
                            </button>
                            {showOptional && data.optional.map((item, i) => (
                                <div key={i} style={{ marginBottom: '0.15rem' }}>
                                    <ItemButton
                                        label={item.label}
                                        done={isDone(item.checkId)}
                                        pendingColor="#888"
                                        onClick={e => { e.stopPropagation(); onNavigate?.(item.tab); }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                </div>
            )}
        </div>
    );
};

export default ExperimentChecklist;
